/**
 * REST client for the generic workflow engine (Plan D §2).
 *
 * Uses the existing admin axios client. Consumer surfaces should
 * substitute their own client; this module only owns URL building +
 * payload typing.
 */

import api from '@/admin/lib/api'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import type {
  ExecuteActionPayload,
  ExecuteActionResult,
  EntityLogEntry,
  ExceptionActionBinding,
  ExceptionActionCatalogEntry,
  ExceptionGrant,
  ExceptionGrantCreatePayload,
  ExceptionReason,
  PendingTransition,
  WorkflowAction,
  WorkflowActionLog,
  WorkflowInstance,
  WorkflowIssue,
  WorkflowIssueDismissPayload,
  WorkflowIssueListParams,
  WorkflowIssueResolvePayload,
} from './types'

const BASE = '/workflows'

export async function getWorkflowInstance(uuid: string): Promise<WorkflowInstance> {
  const res = await api.get<WorkflowInstance>(`${BASE}/instances/${uuid}/`)
  return res.data
}

export async function lookupWorkflowInstance(
  resourceType: string,
  resourceId: string,
): Promise<WorkflowInstance | null> {
  try {
    const res = await api.get<WorkflowInstance>(`${BASE}/instances/lookup/`, {
      params: { resource_type: resourceType, resource_id: resourceId },
    })
    return res.data
  } catch {
    return null
  }
}

export async function getVisibleActions(uuid: string): Promise<WorkflowAction[]> {
  const res = await api.get<WorkflowAction[]>(
    `${BASE}/instances/${uuid}/visible-actions/`,
  )
  return res.data
}

/**
 * Batch variant of {@link getVisibleActions}. Returns a map keyed by
 * instance uuid. Pages rendering many rows that each need a workflow
 * action button should use this rather than calling
 * {@link getVisibleActions} per row, to avoid N HTTP requests per page.
 */
export async function getVisibleActionsBatch(
  uuids: string[],
): Promise<Record<string, WorkflowAction[]>> {
  if (uuids.length === 0) return {}
  const res = await api.post<Record<string, WorkflowAction[]>>(
    `${BASE}/instances/visible-actions/batch/`,
    { instance_uuids: uuids },
  )
  return res.data
}

export async function getWorkflowHistory(uuid: string): Promise<WorkflowActionLog[]> {
  const res = await api.get<WorkflowActionLog[]>(
    `${BASE}/instances/${uuid}/history/`,
  )
  return res.data
}

export async function executeWorkflowAction(
  uuid: string,
  payload: ExecuteActionPayload,
): Promise<ExecuteActionResult> {
  const res = await api.post<ExecuteActionResult>(
    `${BASE}/instances/${uuid}/execute/`,
    payload,
  )
  return res.data
}

export async function listPendingApprovals(): Promise<PendingTransition[]> {
  const res = await api.get<PendingTransition[]>(`${BASE}/pending-approvals/`)
  return res.data
}

export async function approvePending(
  uuid: string,
  payload: { note?: string; correlation_id?: string } = {},
): Promise<ExecuteActionResult> {
  const res = await api.post<ExecuteActionResult>(
    `${BASE}/pending-approvals/${uuid}/approve/`,
    payload,
  )
  return res.data
}

export async function denyPending(
  uuid: string,
  payload: { reason: string },
): Promise<PendingTransition> {
  const res = await api.post<PendingTransition>(
    `${BASE}/pending-approvals/${uuid}/deny/`,
    payload,
  )
  return res.data
}

// ---------------------------------------------------------------------------
// WorkflowIssue
// ---------------------------------------------------------------------------

function buildIssueListParams(
  params: WorkflowIssueListParams = {},
): URLSearchParams {
  // URLSearchParams gives us repeated keys (?status=OPEN&status=RESOLVED)
  // which is what Django's QueryDict.getlist("status") expects. The default
  // axios serializer produces ?status[]=OPEN, which getlist would silently
  // ignore - collapsing all filters and showing every issue regardless of
  // tab. Keep this serializer for every issue query.
  const out = new URLSearchParams()
  if (params.status) {
    const statuses = Array.isArray(params.status) ? params.status : [params.status]
    for (const s of statuses) out.append('status', s)
  }
  if (params.issue_kind) out.set('issue_kind', params.issue_kind)
  if (params.predicate_key) out.set('predicate_key', params.predicate_key)
  if (params.resource_type) out.set('resource_type', params.resource_type)
  if (params.resource_id) out.set('resource_id', params.resource_id)
  if (params.workflow_instance) out.set('workflow_instance', params.workflow_instance)
  if (params.page_size != null) out.set('page_size', String(params.page_size))
  if (params.page != null) out.set('page', String(params.page))
  if (params.search) out.set('search', params.search)
  for (const t of params.record_type ?? []) out.append('record_type', t)
  for (const r of params.reason ?? []) out.append('reason', r)
  if (params.date_preset) out.set('date_preset', params.date_preset)
  return out
}

export async function listWorkflowIssues(
  params: WorkflowIssueListParams = {},
): Promise<WorkflowIssue[]> {
  const res = await api.get<WorkflowIssue[]>(`${BASE}/issues/`, {
    params: buildIssueListParams(params),
  })
  return res.data
}

/** One page of the Exceptions list, with the Filters panel's facets. */
export async function listWorkflowIssuesPage(
  params: WorkflowIssueListParams,
): Promise<{ count: number; results: WorkflowIssue[]; filter_options: ProductFilterFacet[] }> {
  const res = await api.get(`${BASE}/issues/`, { params: buildIssueListParams(params) })
  return res.data
}

export async function getWorkflowIssue(uuid: string): Promise<WorkflowIssue> {
  const res = await api.get<WorkflowIssue>(`${BASE}/issues/${uuid}/`)
  return res.data
}

export async function listInstanceWorkflowIssues(
  instanceUuid: string,
  statuses?: Array<'OPEN' | 'RESOLVED' | 'DISMISSED'>,
): Promise<WorkflowIssue[]> {
  const params = new URLSearchParams()
  if (statuses?.length) {
    for (const s of statuses) params.append('status', s)
  }
  const res = await api.get<WorkflowIssue[]>(
    `${BASE}/instances/${instanceUuid}/issues/`,
    { params },
  )
  return res.data
}

export async function resolveWorkflowIssue(
  uuid: string,
  payload: WorkflowIssueResolvePayload = {},
): Promise<WorkflowIssue> {
  const res = await api.post<WorkflowIssue>(
    `${BASE}/issues/${uuid}/resolve/`,
    payload,
  )
  return res.data
}

export async function dismissWorkflowIssue(
  uuid: string,
  payload: WorkflowIssueDismissPayload = {},
): Promise<WorkflowIssue> {
  const res = await api.post<WorkflowIssue>(
    `${BASE}/issues/${uuid}/dismiss/`,
    payload,
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Exceptions + entity log
// ---------------------------------------------------------------------------

export async function listExceptionReasons(): Promise<ExceptionReason[]> {
  const res = await api.get<ExceptionReason[]>(`${BASE}/exceptions/reasons/`)
  return res.data
}

export async function createExceptionReason(
  payload: Pick<ExceptionReason, 'title' | 'description'>,
): Promise<ExceptionReason> {
  const res = await api.post<ExceptionReason>(`${BASE}/exceptions/reasons/`, payload)
  return res.data
}

export async function updateExceptionReason(
  uuid: string,
  payload: Partial<Pick<ExceptionReason, 'title' | 'description' | 'is_active' | 'order'>>,
): Promise<ExceptionReason> {
  const res = await api.patch<ExceptionReason>(
    `${BASE}/exceptions/reasons/${uuid}/`,
    payload,
  )
  return res.data
}

export async function deleteExceptionReason(uuid: string): Promise<void> {
  await api.delete(`${BASE}/exceptions/reasons/${uuid}/`)
}

export async function listExceptionActionBindings(): Promise<ExceptionActionBinding[]> {
  const res = await api.get<ExceptionActionBinding[]>(
    `${BASE}/exceptions/action-bindings/`,
  )
  return res.data
}

export async function createExceptionActionBinding(payload: {
  action_key: string
  action_label?: string
  resource_type?: string
  workflow_action?: string | null
  suggested_exception?: string | null
}): Promise<ExceptionActionBinding> {
  const res = await api.post<ExceptionActionBinding>(
    `${BASE}/exceptions/action-bindings/`,
    payload,
  )
  return res.data
}

export async function updateExceptionActionBinding(
  uuid: string,
  payload: {
    action_key?: string
    action_label?: string
    resource_type?: string
    workflow_action?: string | null
    suggested_exception?: string | null
  },
): Promise<ExceptionActionBinding> {
  const res = await api.patch<ExceptionActionBinding>(
    `${BASE}/exceptions/action-bindings/${uuid}/`,
    payload,
  )
  return res.data
}

export async function deleteExceptionActionBinding(uuid: string): Promise<void> {
  await api.delete(`${BASE}/exceptions/action-bindings/${uuid}/`)
}

export async function listExceptionActionCatalog(): Promise<
  ExceptionActionCatalogEntry[]
> {
  const res = await api.get<ExceptionActionCatalogEntry[]>(
    `${BASE}/exceptions/action-catalog/`,
  )
  return res.data
}

export async function createExceptionGrant(
  payload: ExceptionGrantCreatePayload,
): Promise<ExceptionGrant> {
  const res = await api.post<ExceptionGrant>(`${BASE}/exceptions/grants/`, payload)
  return res.data
}

export async function listEntityLog(params: {
  entity_key?: string
  entity_id?: string
  workflow_instance?: string
  page_size?: number
}): Promise<EntityLogEntry[]> {
  const res = await api.get<EntityLogEntry[]>(`${BASE}/entity-log/`, { params })
  return res.data
}
